import { useState, useEffect, useRef } from "react";
import { MessageSquare, X, Send, Ticket, LifeBuoy, Check, AlertCircle, ChevronRight } from "lucide-react";
import api, { formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

export default function SupportWidget({ user }) {
  const [isOpen, setIsOpen] = useState(false);
  const [tab, setTab] = useState("chat"); // 'chat' | 'tickets' | 'create_ticket'
  const [messages, setMessages] = useState([
    { sender: "bot", text: `नमस्ते ${user?.name || "साथी"}, मैं आपका Sathi AI support assistant हूँ। मैं आपकी क्या मदद कर सकता हूँ?` }
  ]);
  const [inputText, setInputText] = useState("");
  const [loading, setLoading] = useState(false);
  
  // Tickets state
  const [tickets, setTickets] = useState([]);
  const [activeTicket, setActiveTicket] = useState(null);
  const [ticketSubject, setTicketSubject] = useState("");
  const [ticketDesc, setTicketDesc] = useState("");
  const [ticketPriority, setTicketPriority] = useState("medium");
  const [replyText, setReplyText] = useState("");
  const [creatingTicket, setCreatingTicket] = useState(false);
  const [sendingReply, setSendingReply] = useState(false);

  const chatEndRef = useRef(null);

  // Scroll to bottom of chat
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, isOpen, tab, activeTicket]);

  // Handle global event to open widget (e.g. from navbar click)
  useEffect(() => {
    const handleOpenSupport = () => {
      setIsOpen(true);
    };
    window.addEventListener("open_support_chat", handleOpenSupport);
    return () => window.removeEventListener("open_support_chat", handleOpenSupport);
  }, []);

  // Fetch user tickets when switching to tickets tab
  useEffect(() => {
    if (tab === "tickets" && isOpen) {
      fetchTickets();
    }
  }, [tab, isOpen]);

  const fetchTickets = async () => {
    try {
      const res = await api.get("/support/tickets");
      setTickets(res.data || []);
    } catch (err) {
      console.error("Failed to fetch tickets", err);
    }
  };

  const handleSendMessage = async (textToSend) => {
    const text = textToSend || inputText.trim();
    if (!text) return;

    if (!textToSend) setInputText("");

    const newMsg = { sender: "user", text };
    setMessages((prev) => [...prev, newMsg]);
    setLoading(true);

    try {
      const res = await api.post("/support/chat", {
        message: text,
        history: messages.map((m) => ({ sender: m.sender, text: m.text }))
      });

      if (res.data?.response) {
        setMessages((prev) => [...prev, { sender: "bot", text: res.data.response }]);
      }
      if (res.data?.ticket_created) {
        toast.success("Support Ticket details logged!");
        fetchTickets();
      }
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTicketSubmit = async (e) => {
    e.preventDefault();
    if (!ticketSubject.trim() || !ticketDesc.trim()) {
      return toast.error("Please enter a subject and details.");
    }
    setCreatingTicket(true);
    try {
      await api.post("/support/tickets", {
        subject: ticketSubject,
        description: ticketDesc,
        priority: ticketPriority
      });
      toast.success("Support ticket raised successfully!");
      setTicketSubject("");
      setTicketDesc("");
      setTab("tickets");
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setCreatingTicket(false);
    }
  };

  const handleSendTicketReply = async (e) => {
    e.preventDefault();
    if (!replyText.trim()) return;
    setSendingReply(true);
    try {
      const res = await api.post(`/support/tickets/${activeTicket.ticket_id}/message`, {
        text: replyText
      });
      setActiveTicket((prev) => ({
        ...prev,
        status: "open",
        messages: [...(prev.messages || []), res.data.message]
      }));
      setReplyText("");
      fetchTickets();
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setSendingReply(false);
    }
  };

  const selectTicket = async (tkt) => {
    try {
      const res = await api.get(`/support/tickets/${tkt.ticket_id}`);
      setActiveTicket(res.data);
    } catch (err) {
      toast.error("Failed to load ticket details");
    }
  };

  const presetPrompts = [
    { text: "Verification कैसे कराएं?", value: "How to verify profile?" },
    { text: "Premium membership के फायदे?", value: "Premium membership benefits?" },
    { text: "Ticket raise करना है / Report issue", value: "Raise a support ticket" }
  ];

  return (
    <div className="fixed bottom-6 right-6 z-[9990] flex flex-col items-end">
      {/* Floating Button */}
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          className="w-14 h-14 rounded-full bg-gradient-to-tr from-[#6b1a24] to-[#c0392b] text-white flex items-center justify-center shadow-xl hover:shadow-2xl hover:scale-105 transition-all animate-bounce"
          aria-label="Support Help Desk"
        >
          <MessageSquare className="w-6 h-6" />
        </button>
      )}

      {/* Support Chat Card */}
      {isOpen && (
        <div className="w-[92vw] max-w-[380px] h-[500px] bg-white dark:bg-zinc-950 rounded-3xl shadow-2xl border border-border/80 flex flex-col overflow-hidden animate-in slide-in-from-bottom-8 fade-in duration-300">
          {/* Header */}
          <div className="bg-gradient-to-r from-[#6b1a24] to-[#c0392b] p-4 text-white flex items-center justify-between shadow-md">
            <div className="flex items-center gap-2.5">
              <div className="w-9 h-9 rounded-full bg-white/20 flex items-center justify-center ring-2 ring-white/10">
                <LifeBuoy className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="font-bold text-sm leading-tight">Sathi AI Desk</h3>
                <span className="text-[10px] text-white/80 flex items-center gap-1 mt-0.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />
                  Online & Active
                </span>
              </div>
            </div>
            
            <div className="flex items-center gap-1">
              <button
                onClick={() => setIsOpen(false)}
                className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
                aria-label="Close"
              >
                <X className="w-4 h-4 text-white" />
              </button>
            </div>
          </div>

          {/* Navigation Tabs */}
          <div className="flex border-b border-border bg-muted/20 text-xs">
            <button
              onClick={() => { setTab("chat"); setActiveTicket(null); }}
              className={`flex-1 py-3 font-semibold transition-colors border-b-2 text-center ${tab === "chat" && !activeTicket ? "border-[#c0392b] text-[#c0392b] bg-white dark:bg-zinc-950" : "border-transparent text-muted-foreground hover:bg-muted/40"}`}
            >
              AI Assistant
            </button>
            <button
              onClick={() => { setTab("tickets"); setActiveTicket(null); }}
              className={`flex-1 py-3 font-semibold transition-colors border-b-2 text-center ${tab === "tickets" || activeTicket ? "border-[#c0392b] text-[#c0392b] bg-white dark:bg-zinc-950" : "border-transparent text-muted-foreground hover:bg-muted/40"}`}
            >
              My Tickets
            </button>
          </div>

          {/* Body Content */}
          <div className="flex-1 overflow-y-auto p-4 bg-slate-50/50 dark:bg-zinc-900/10">
            {/* TAB: Chat */}
            {tab === "chat" && !activeTicket && (
              <div className="space-y-4 flex flex-col min-h-full">
                <div className="flex-1 space-y-3.5">
                  {messages.map((m, idx) => (
                    <div key={idx} className={`flex ${m.sender === "user" ? "justify-end" : "justify-start"}`}>
                      <div
                        className={`max-w-[80%] rounded-2xl px-3.5 py-2 text-xs leading-relaxed shadow-sm ${
                          m.sender === "user"
                            ? "bg-[#6b1a24] text-white rounded-tr-none"
                            : "bg-white dark:bg-zinc-900 text-foreground border border-border/60 rounded-tl-none"
                        }`}
                      >
                        {m.text}
                      </div>
                    </div>
                  ))}

                  {loading && (
                    <div className="flex justify-start">
                      <div className="bg-white dark:bg-zinc-900 border border-border/60 rounded-2xl rounded-tl-none px-4 py-2 flex items-center gap-1.5 shadow-sm">
                        <span className="w-1.5 h-1.5 rounded-full bg-[#6b1a24] animate-bounce" />
                        <span className="w-1.5 h-1.5 rounded-full bg-[#6b1a24] animate-bounce [animation-delay:0.2s]" />
                        <span className="w-1.5 h-1.5 rounded-full bg-[#6b1a24] animate-bounce [animation-delay:0.4s]" />
                      </div>
                    </div>
                  )}
                  <div ref={chatEndRef} />
                </div>

                {/* Preset helpers */}
                {messages.length < 5 && !loading && (
                  <div className="pt-2 space-y-1.5">
                    <p className="text-[10px] text-muted-foreground font-semibold uppercase tracking-wider mb-1">Quick Options:</p>
                    <div className="flex flex-wrap gap-1.5">
                      {presetPrompts.map((p, i) => (
                        <button
                          key={i}
                          onClick={() => handleSendMessage(p.value)}
                          className="text-[11px] bg-white hover:bg-slate-100 border border-border/60 text-foreground px-3 py-1.5 rounded-full shadow-sm text-left transition-colors"
                        >
                          {p.text}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* TAB: Ticket Details */}
            {activeTicket && (
              <div className="space-y-4 flex flex-col min-h-full">
                <div className="flex items-center justify-between border-b border-border pb-2">
                  <button onClick={() => setActiveTicket(null)} className="text-xs text-[#c0392b] font-semibold hover:underline">
                    ← Back to list
                  </button>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold uppercase ${
                    activeTicket.status === "open" ? "bg-amber-100 text-amber-700" :
                    activeTicket.status === "resolved" ? "bg-emerald-100 text-emerald-700" : "bg-zinc-200 text-zinc-700"
                  }`}>
                    {activeTicket.status}
                  </span>
                </div>

                <div className="bg-white dark:bg-zinc-900 border border-border/60 rounded-xl p-3 shadow-sm">
                  <h4 className="font-bold text-xs text-foreground">{activeTicket.subject}</h4>
                  <p className="text-[11px] text-muted-foreground mt-1 leading-relaxed">{activeTicket.description}</p>
                </div>

                {/* Ticket Message History */}
                <div className="flex-1 space-y-3.5">
                  {(activeTicket.messages || []).map((msg, idx) => (
                    <div key={idx} className={`flex ${msg.sender === "user" ? "justify-end" : "justify-start"}`}>
                      <div className="max-w-[85%]">
                        <div
                          className={`rounded-2xl px-3.5 py-2 text-xs leading-relaxed shadow-sm ${
                            msg.sender === "user"
                              ? "bg-[#6b1a24] text-white rounded-tr-none"
                              : "bg-emerald-50 dark:bg-zinc-900 text-foreground border border-border/60 rounded-tl-none"
                          }`}
                        >
                          {msg.text}
                        </div>
                        <span className="text-[9px] text-muted-foreground/60 mt-1 block px-1">
                          {msg.sender === "user" ? "You" : "Support Admin"} • {new Date(msg.created_at).toLocaleTimeString("en-IN", { hour: '2-digit', minute: '2-digit' })}
                        </span>
                      </div>
                    </div>
                  ))}
                  <div ref={chatEndRef} />
                </div>
              </div>
            )}

            {/* TAB: Ticket List */}
            {tab === "tickets" && !activeTicket && (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider">Tickets Lodged</h4>
                  <button
                    onClick={() => setTab("create_ticket")}
                    className="text-xs bg-[#c0392b] text-white px-3 py-1 rounded-full font-bold hover:bg-[#a93226] transition-colors"
                  >
                    Raise Ticket
                  </button>
                </div>

                {tickets.length === 0 ? (
                  <div className="text-center py-12 space-y-2 border border-dashed border-border/80 rounded-2xl bg-white dark:bg-zinc-950 mt-2">
                    <Ticket className="w-8 h-8 text-muted-foreground/40 mx-auto" />
                    <p className="text-xs text-muted-foreground">कोई open tickets नहीं मिले।</p>
                  </div>
                ) : (
                  <div className="space-y-2.5">
                    {tickets.map((tkt) => (
                      <div
                        key={tkt.ticket_id}
                        onClick={() => selectTicket(tkt)}
                        className="bg-white dark:bg-zinc-900 border border-border/60 rounded-2xl p-3 shadow-sm hover:border-[#c0392b]/50 cursor-pointer transition-all flex items-center justify-between"
                      >
                        <div className="min-w-0 flex-1 pr-2">
                          <h5 className="font-bold text-xs text-foreground truncate">{tkt.subject}</h5>
                          <p className="text-[10px] text-muted-foreground truncate mt-0.5">{tkt.description}</p>
                          <span className="text-[9px] text-muted-foreground/60 mt-1 block">
                            Updated: {new Date(tkt.updated_at).toLocaleDateString("en-IN")}
                          </span>
                        </div>
                        <div className="flex flex-col items-end gap-1.5 shrink-0">
                          <span className={`text-[9px] px-2 py-0.5 rounded-full font-bold uppercase ${
                            tkt.status === "open" ? "bg-amber-100 text-amber-700" :
                            tkt.status === "resolved" ? "bg-emerald-100 text-emerald-700" : "bg-zinc-200 text-zinc-700"
                          }`}>
                            {tkt.status}
                          </span>
                          <ChevronRight className="w-3.5 h-3.5 text-muted-foreground" />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* TAB: Create Ticket Form */}
            {tab === "create_ticket" && (
              <form onSubmit={handleCreateTicketSubmit} className="space-y-4">
                <div className="flex items-center justify-between">
                  <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider">New Ticket</h4>
                  <button type="button" onClick={() => setTab("tickets")} className="text-xs text-[#c0392b] font-semibold hover:underline">
                    Cancel
                  </button>
                </div>

                <div className="space-y-3.5 bg-white dark:bg-zinc-950 border border-border/60 rounded-2xl p-4 shadow-sm">
                  <div>
                    <label className="text-[10px] font-bold text-foreground/80 uppercase tracking-wider">Ticket Subject</label>
                    <Input
                      value={ticketSubject}
                      onChange={(e) => setTicketSubject(e.target.value)}
                      placeholder="e.g. Premium payment issue"
                      className="mt-1 text-xs"
                      required
                    />
                  </div>

                  <div>
                    <label className="text-[10px] font-bold text-foreground/80 uppercase tracking-wider">Details / Description</label>
                    <Textarea
                      value={ticketDesc}
                      onChange={(e) => setTicketDesc(e.target.value)}
                      placeholder="Explain your problem clearly..."
                      className="mt-1 text-xs resize-none"
                      rows={4}
                      required
                    />
                  </div>

                  <div>
                    <label className="text-[10px] font-bold text-foreground/80 uppercase tracking-wider">Priority</label>
                    <div className="flex gap-2 mt-1">
                      {["low", "medium", "high"].map((p) => (
                        <button
                          key={p}
                          type="button"
                          onClick={() => setTicketPriority(p)}
                          className={`flex-1 py-1 rounded-lg text-xs font-semibold border transition-all uppercase ${
                            ticketPriority === p
                              ? "bg-primary text-white border-primary"
                              : "border-border hover:bg-muted text-muted-foreground"
                          }`}
                        >
                          {p}
                        </button>
                      ))}
                    </div>
                  </div>

                  <Button
                    type="submit"
                    disabled={creatingTicket}
                    className="w-full bg-[#c0392b] hover:bg-[#a93226] text-white text-xs font-bold rounded-xl mt-2 h-10 shadow-md"
                  >
                    {creatingTicket ? "Raising Ticket..." : "Submit Ticket"}
                  </Button>
                </div>
              </form>
            )}
          </div>

          {/* Footer Input Area */}
          {tab === "chat" && !activeTicket && (
            <form
              onSubmit={(e) => { e.preventDefault(); handleSendMessage(); }}
              className="p-3 border-t border-border bg-white dark:bg-zinc-950 flex items-center gap-2"
            >
              <Input
                value={inputText}
                onChange={(e) => setInputText(e.target.value)}
                placeholder="Type details or ask anything..."
                className="flex-1 text-xs h-9 rounded-xl focus-visible:ring-0 focus-visible:ring-offset-0 focus:border-[#c0392b]/60"
              />
              <button
                type="submit"
                disabled={loading || !inputText.trim()}
                className="w-9 h-9 rounded-xl bg-gradient-to-tr from-[#6b1a24] to-[#c0392b] text-white flex items-center justify-center hover:scale-105 disabled:opacity-50 transition-all shadow"
              >
                <Send className="w-4 h-4" />
              </button>
            </form>
          )}

          {/* Ticket Reply Input Area */}
          {activeTicket && activeTicket.status !== "closed" && (
            <form
              onSubmit={handleSendTicketReply}
              className="p-3 border-t border-border bg-white dark:bg-zinc-950 flex items-center gap-2"
            >
              <Input
                value={replyText}
                onChange={(e) => setReplyText(e.target.value)}
                placeholder="Reply to support ticket..."
                className="flex-1 text-xs h-9 rounded-xl focus-visible:ring-0 focus-visible:ring-offset-0 focus:border-[#c0392b]/60"
              />
              <button
                type="submit"
                disabled={sendingReply || !replyText.trim()}
                className="w-9 h-9 rounded-xl bg-gradient-to-tr from-[#6b1a24] to-[#c0392b] text-white flex items-center justify-center hover:scale-105 disabled:opacity-50 transition-all shadow"
              >
                <Send className="w-4 h-4" />
              </button>
            </form>
          )}
        </div>
      )}
    </div>
  );
}
